import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { useNotification } from '../components/Notification';
import Spinner from '../components/Spinner';
import IconButton from '../components/IconButton';
import DataTable from '../components/DataTable';
import './Tags.css';

function Tags({ tenantId }) {
  const { showNotification } = useNotification();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    color: '#FF5733'
  });

  useEffect(() => {
    if (tenantId) {
      loadTags();
    }
  }, [tenantId]);

  const loadTags = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get('/tags', tenantId);
      setTags(data);
    } catch (err) {
      setError(err.message || 'Fehler beim Laden der Tags');
      showNotification(err.message || 'Fehler beim Laden der Tags', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      await api.post('/tags', formData, tenantId);
      showNotification('Tag erfolgreich erstellt', 'success');
      setShowCreateModal(false);
      setFormData({ name: '', color: '#FF5733' });
      loadTags();
    } catch (err) {
      setError(err.message || 'Fehler beim Erstellen des Tags');
      showNotification(err.message || 'Fehler beim Erstellen des Tags', 'error');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      await api.put(`/tags/${editingTag.id}`, formData, tenantId);
      showNotification('Tag erfolgreich aktualisiert', 'success');
      setEditingTag(null);
      setFormData({ name: '', color: '#FF5733' });
      loadTags();
    } catch (err) {
      setError(err.message || 'Fehler beim Aktualisieren des Tags');
      showNotification(err.message || 'Fehler beim Aktualisieren des Tags', 'error');
    }
  };

  const handleDelete = async (tagId) => {
    if (!window.confirm('Möchten Sie diesen Tag wirklich löschen?')) {
      return;
    }
    setError(null);
    try {
      await api.delete(`/tags/${tagId}`, tenantId);
      showNotification('Tag erfolgreich gelöscht', 'success');
      loadTags();
    } catch (err) {
      setError(err.message || 'Fehler beim Löschen des Tags');
      showNotification(err.message || 'Fehler beim Löschen des Tags', 'error');
    }
  };

  const handleEdit = (tag) => {
    setEditingTag(tag);
    setFormData({
      name: tag.name,
      color: tag.color
    });
    setShowCreateModal(true);
  };

  const handleCancel = () => {
    setShowCreateModal(false);
    setEditingTag(null);
    setFormData({ name: '', color: '#FF5733' });
  };

  const presetColors = [
    '#FF5733', '#33FF57', '#3357FF', '#FF33F5', '#F5FF33',
    '#FF3366', '#33FFF5', '#FF8C33', '#8C33FF', '#33FF8C',
    '#FF3333', '#33FF33', '#3333FF', '#FFFF33', '#FF33FF'
  ];

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="tags-page">
      <div className="page-header">
        <h1>Tags</h1>
        <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
          <IconButton icon="add" /> Neuer Tag
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <DataTable
        data={tags}
        columns={[
          {
            key: 'actions',
            header: '',
            sortable: false,
            width: '100px',
            render: (row) => (
              <div style={{ display: 'inline-flex', gap: '0.25rem', whiteSpace: 'nowrap' }}>
                <IconButton
                  icon="edit"
                  onClick={() => handleEdit(row)}
                  title="Bearbeiten"
                />
                <IconButton
                  icon="delete"
                  onClick={() => handleDelete(row.id)}
                  title="Löschen"
                  className="danger"
                />
              </div>
            )
          },
          {
            key: 'color',
            header: 'Farbe',
            sortable: false,
            width: '80px',
            render: (row) => (
              <div 
                style={{ 
                  width: '32px', 
                  height: '32px', 
                  borderRadius: '6px', 
                  backgroundColor: row.color,
                  border: '2px solid rgba(0, 0, 0, 0.1)',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}
                title={row.color.toUpperCase()}
              />
            )
          },
          {
            key: 'name',
            header: 'Name',
            sortType: 'string',
            accessor: (row) => row.name || ''
          },
          {
            key: 'colorCode',
            header: 'Farbcode',
            sortType: 'string',
            accessor: (row) => row.color?.toUpperCase() || '',
            render: (row) => (
              <span style={{ fontFamily: 'monospace', fontSize: '0.9rem', color: '#64748b' }}>
                {row.color?.toUpperCase() || ''}
              </span>
            )
          }
        ]}
        defaultSort={{ key: 'name', direction: 'asc' }}
        emptyMessage="Keine Tags vorhanden"
      />

      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>{editingTag ? 'Tag bearbeiten' : 'Neuen Tag erstellen'}</h2>
            <form onSubmit={editingTag ? handleUpdate : handleCreate}>
              <div className="form-group">
                <label htmlFor="tagName">Name</label>
                <input
                  type="text"
                  id="tagName"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="z.B. VIP, Wartung, Dringend"
                />
              </div>

              <div className="form-group">
                <label htmlFor="tagColor">Farbe</label>
                <div className="color-input-group">
                  <input
                    type="color"
                    id="tagColor"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    required
                  />
                  <input
                    type="text"
                    value={formData.color}
                    onChange={(e) => {
                      if (/^#[0-9A-Fa-f]{6}$/i.test(e.target.value)) {
                        setFormData({ ...formData, color: e.target.value.toUpperCase() });
                      }
                    }}
                    placeholder="#FF5733"
                    pattern="^#[0-9A-Fa-f]{6}$"
                  />
                </div>
                <div className="preset-colors">
                  {presetColors.map(color => (
                    <button
                      key={color}
                      type="button"
                      className="preset-color-btn"
                      style={{ backgroundColor: color }}
                      onClick={() => setFormData({ ...formData, color })}
                      title={color}
                    />
                  ))}
                </div>
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn-primary">
                  {editingTag ? 'Aktualisieren' : 'Erstellen'}
                </button>
                <button type="button" className="btn-secondary" onClick={handleCancel}>
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Tags;

